import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import { Phone, MessageCircle, RefreshCw, Smartphone, Plus, Trash2, Mic, MicOff, ChevronDown, ChevronLeft, ChevronRight, UserCheck, CalendarCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  getNurtureBoard, nurtureSyncNow, saveNurturePhones, callNurtureLead, nurtureHeadsUp,
  setNurtureOutcome, listNurtureCalls, getNurtureCall, nurtureCallAudioUrl, reprocessNurtureCall, getNurtureTeam,
  getNurtureScript, saveNurtureScript, getNurtureConsultHours, getNurtureSettings, saveNurtureSettings, nurtureTestCall,
  getNurtureHeadsUpStatus, resolveNurtureHeadsUp, getVoigerStatus, voigerSyncNow,
  nurtureResolveAttention,
} from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import NurtureAnalytics from "@/components/NurtureAnalytics";
import NurtureCalendar from "@/components/NurtureCalendar";
import { TeamToday, TeamManage, ShiftsTab, TodayLine } from "@/components/NurtureTeam";
import { LeadNotes, JustCalled, LeadSearch, IntakeStatusDialog } from "@/components/NurtureTools";
import IncentiveCountries from "@/components/IncentiveCountries";
import { AssistantTab, AgentLine, GhlBotWarning } from "@/components/NurtureAssistant";
import { AiHandoffs, AiHandoverColumn, AiCallerPanel } from "@/components/AiCaller";
import { atLeast } from "@/lib/roles";

const WHATSAPP = "ghl_whatsapp";
const OUTCOMES = [
  ["booked", "Consult booked"], ["callback", "Call back later"], ["no_answer", "No answer"],
  ["not_interested", "Not interested"], ["wrong_number", "Wrong number"],
];
// Confirmation call before a booked consult (routes/nurture.py, "confirmation calls").
const CONFIRM_OUTCOMES = [
  ["confirmed", "Confirmed"], ["reschedule", "Wants to reschedule"], ["cancelling", "Cancelling"], ["no_answer", "No answer"],
];
const OUTCOME_LABEL = { ...Object.fromEntries(OUTCOMES), ...Object.fromEntries(CONFIRM_OUTCOMES), no_show: "No-show" };
const MODE_LABEL = { exotel: "Exotel · recorded", direct: "Direct · not recorded", whatsapp: "WhatsApp · not recorded", voiger: "Voiger · recorded" };
const err = (e, fallback) => e?.response?.data?.detail || fallback;

function ago(iso) {
  if (!iso) return "";
  const m = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 60000));
  if (m < 1) return "just now";
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  return h < 24 ? `${h} h ago` : `${Math.round(h / 24)} d ago`;
}

const shortGym = (name) => (name || "").split(" - ").pop();

// "Came in" is when GoHighLevel got the lead; if FPA only saw it a while later, say so (CR-0055).
function cameIn(lead) {
  const made = new Date(lead.ghl_created_at || 0).getTime();
  const added = new Date(lead.added_at || 0).getTime();
  const late = made && added && added - made > 5 * 60000 && !lead.repeat_of;
  return `Came in ${ago(lead.ghl_created_at)}${late ? ` · on the board ${ago(lead.added_at)}` : ""}`;
}

// Same person again (CR-0054 / CR-0057): one card, labelled, instead of a second "new" card.
function EnquiredAgain({ lead }) {
  const prev = lead.repeat_of;
  const more = (lead.repeat_enquiries || []).length;
  if (!prev && !lead.enquired_again_at) return null;
  const before = prev
    ? ` · first came in ${ago(prev.ghl_created_at || prev.added_at)}${prev.attempts ? `, called ${prev.attempts}×` : ""}${prev.last_caller_name ? ` by ${prev.last_caller_name.split(" ")[0]}` : ""}${prev.done_reason ? ` (${prev.done_reason})` : ""}`
    : more ? ` · filled the form ${more + 1} times` : "";
  return (
    <div className="mt-1 text-[11px] text-amber-300" data-testid={`enquired-again-${lead.id}`}
      title="The same person (same phone or GoHighLevel contact) enquired again">
      Enquired again {ago(lead.enquired_again_at || lead.ghl_created_at)}{before}
    </div>
  );
}

// Redial standard (routes/nurture.py): the next call's time in the gym's timezone, e.g. "6:30 PM" or "Tue 9:30 AM".
function gymTime(iso, tz) {
  if (!iso) return "";
  const d = new Date(iso);
  const opts = tz ? { timeZone: tz } : {};
  try {
    const time = d.toLocaleTimeString([], { ...opts, hour: "numeric", minute: "2-digit" });
    const sameDay = d.toLocaleDateString([], opts) === new Date().toLocaleDateString([], opts);
    return sameDay ? time : `${d.toLocaleDateString([], { ...opts, weekday: "short" })} ${time}`;
  } catch {
    return d.toLocaleString([], { hour: "numeric", minute: "2-digit" });
  }
}
// "Call back later" (set by the rep, or chosen by the lead on WhatsApp) whose time has come.
const callbackDue = (l) => l.status !== "done" && l.last_outcome === "callback" && !!l.callback_at
  && new Date(l.callback_at).getTime() <= Date.now();
const redialDue = (l) => {
  const r = l.redial;
  return !!(r?.kind && (r.due === "due" || r.due === "overdue")
    && (r.kind === "four_week_callback" || r.kind === "reschedule" || r.done_attempts > 0)) || callbackDue(l);
};
const dueAt = (l) => new Date(l.redial?.next_at || l.callback_at || 0).getTime();
// Waiting for a later call (a callback, e.g. after "Not now" on WhatsApp, or the next redial): such leads sit at the
// bottom of the column with their next call time and come back up once it's time to call.
const waitingUntil = (l) => {
  if (l.status === "done" || redialDue(l)) return 0;
  const t = l.last_outcome === "callback" && l.callback_at ? new Date(l.callback_at).getTime()
    : l.redial?.due === "later" && l.redial?.next_at ? new Date(l.redial.next_at).getTime() : 0;
  return t > Date.now() ? t : 0;
};
// A WhatsApp reply belongs to this call attempt if it came after the rep pressed Call (a minute's clock slack).
const freshReply = (st, startedAt) => !!st?.reply_at && new Date(st.reply_at).getTime() > (startedAt || 0) - 60000;
// datetime-local value in the browser's time
const localInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};
// Another rep's call in progress (soft lock, a few minutes): "Indu is calling…".
const lockedBy = (l, me) => {
  const c = l.active_call;
  return c && c.user_id !== me?.user_id && new Date(c.until).getTime() > Date.now() ? c : null;
};
const firstName = (n) => (n || "").split(" ")[0];
const AGAIN_MINUTES = 10;                 // a second dial this soon after the first goes straight out (no heads-up)
const HOLD_MINUTES = 15;                  // a called lead keeps its place on the board until the outcome is saved
const calledRecently = (l, me) => l.last_caller_id === me?.user_id && !!l.last_called_at
  && Date.now() - new Date(l.last_called_at).getTime() < AGAIN_MINUTES * 60000;
// While a lead is held (being called, outcome not saved yet) the board files and sorts it as it was before the call.
const HELD_FIELDS = ["attempts", "status", "redial", "last_outcome", "callback_at", "scheduled"];
const confirmDue = (l) => l.confirm?.state === "due";
// Owner rule (2026-10-19): a lead with an upcoming appointment is only on Confirmations, not Open / New / Redials due.
const scheduled = (l) => !!l.scheduled;
// Confirmations, grouped per day (gym time) and Morning / Evening, in appointment order.
function confirmGroups(leads) {
  const out = [];
  leads.forEach((l) => {
    const c = l.confirm;
    let day = "";
    let hour = 12;
    try {
      const d = new Date(c.appt_at);
      day = d.toLocaleDateString("en-CA", c.tz ? { timeZone: c.tz } : {});
      hour = Number(d.toLocaleTimeString("en-GB", { ...(c.tz ? { timeZone: c.tz } : {}), hour: "2-digit", hourCycle: "h23" }).slice(0, 2));
    } catch { /* bad tz */ }
    const session = c.rule ? (c.rule === "morning" ? "Morning" : "Evening") : (hour < 12 ? "Morning" : "Evening");
    const key = `${day} ${session}`;
    const last = out[out.length - 1];
    if (last && last.key === key) last.leads.push(l);
    else out.push({ key, label: `${consultDay(c.appt_at, c.tz)} · ${session}`, leads: [l] });
  });
  return out;
}
// "today 6:30 PM" / "tomorrow 7:30 AM" / "Fri 16 Oct 6:00 PM", in the gym's timezone.
function consultWhen(iso, tz) {
  if (!iso) return "";
  const opts = tz ? { timeZone: tz } : {};
  try {
    const d = new Date(iso);
    const time = d.toLocaleTimeString([], { ...opts, hour: "numeric", minute: "2-digit" });
    const day = (x) => x.toLocaleDateString("en-CA", opts);
    if (day(d) === day(new Date())) return `today ${time}`;
    if (day(d) === day(new Date(Date.now() + 86400000))) return `tomorrow ${time}`;
    return `${d.toLocaleDateString([], { ...opts, weekday: "short", day: "numeric", month: "short" })} ${time}`;
  } catch {
    return new Date(iso).toLocaleString();
  }
}
// "Today" / "Tomorrow" / "Fri 16 Oct", in the gym's timezone.
function consultDay(iso, tz) {
  const opts = tz ? { timeZone: tz } : {};
  try {
    const d = new Date(iso);
    const day = (x) => x.toLocaleDateString("en-CA", opts);
    if (day(d) === day(new Date())) return "Today";
    if (day(d) === day(new Date(Date.now() + 86400000))) return "Tomorrow";
    return d.toLocaleDateString([], { ...opts, weekday: "short", day: "numeric", month: "short" });
  } catch {
    return "";
  }
}
function apptTime(iso, tz) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString([], { ...(tz ? { timeZone: tz } : {}), weekday: "short", day: "numeric", month: "short", hour: "numeric", minute: "2-digit" });
  } catch {
    return new Date(iso).toLocaleString();
  }
}

// Country tabs on the board. The API sends each gym's country already normalised (UAE → Dubai).
const COUNTRIES = ["India", "Sri Lanka", "Dubai"];
const ALL = "All";
const tabKey = (uid) => `fpa.nurture.country.${uid || "anon"}`;
function readTab(uid) {
  try { return window.localStorage.getItem(tabKey(uid)) || ALL; } catch { return ALL; }
}
function saveTab(uid, v) {
  try { window.localStorage.setItem(tabKey(uid), v); } catch { /* private mode etc. */ }
}
const secs = (s) => (s == null ? "—" : `${Math.floor(s / 60)}:${String(Math.round(s % 60)).padStart(2, "0")}`);

function beep() {
  try {
    const ctx = new (window.AudioContext || window.webkitAudioContext)();
    const o = ctx.createOscillator(); const g = ctx.createGain();
    o.frequency.value = 880; g.gain.value = 0.08; o.connect(g); g.connect(ctx.destination);
    o.start(); o.stop(ctx.currentTime + 0.25);
  } catch { /* no audio */ }
}

export default function LeadNurture() {
  const { user } = useAuth();
  const uid = user?.user_id;
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("board");
  const [view, setView] = useState("open");
  const [country, setCountryState] = useState(() => readTab(uid));
  useEffect(() => { setCountryState(readTab(uid)); }, [uid]);
  const setCountry = (c) => { setCountryState(c); saveTab(uid, c); };
  const [allGyms, setAllGyms] = useState(false);               // "Show all gyms" when the rep has gyms set (Team)
  const [testOpen, setTestOpen] = useState(false);
  const [callsRep, setCallsRep] = useState(null);              // {user_id, name}: Calls tab filtered to one rep (from Analytics)
  const [phonesOpen, setPhonesOpen] = useState(false);
  const [outcomeFor, setOutcomeFor] = useState(null);          // {call, lead}
  const [calling, setCalling] = useState(null);                // {lead, phoneId, phoneLabel, until, headsUp, result, reply, declined, again}
  const [held, setHeld] = useState({});                        // lead id → {snap, at}: keeps its place until the outcome is saved
  const [notesVersion, setNotesVersion] = useState(0);         // bumped after an outcome is saved: open notes reload
  const [intakeOpen, setIntakeOpen] = useState(false);
  const known = useRef(null);
  const callNow = useRef(null);                                // lead ids the assistant marked "wants a call now"
  const hold = useCallback((lead) => setHeld((h) => (h[lead.id] ? h : {
    ...h, [lead.id]: { snap: Object.fromEntries(HELD_FIELDS.map((k) => [k, lead[k]])), at: Date.now() } })), []);
  const release = useCallback((leadId) => setHeld((h) => {
    if (!leadId || !h[leadId]) return h;
    const { [leadId]: _gone, ...rest } = h;
    return rest;
  }), []);

  const load = useCallback(async () => {
    try {
      const d = await getNurtureBoard(allGyms);
      const ids = new Set(d.gyms.flatMap((g) => g.leads.filter((l) => l.status === "new").map((l) => l.id)));
      if (known.current) {
        const fresh = d.gyms.flatMap((g) => g.leads.filter((l) => l.status === "new" && !known.current.has(l.id)
          && l.redial?.kind !== "four_week_callback").map((l) => [l, g]));
        if (fresh.length) {
          beep();
          fresh.slice(0, 3).forEach(([l, g]) => toast(`New lead · ${shortGym(g.name)}`, {
            description: [l.name, l.area && `from ${l.area}`, l.campaign || l.source].filter(Boolean).join(" · ") }));
        }
      }
      known.current = ids;
      // The assistant marked a lead "wants a call now" (WhatsApp / Instagram / Facebook): tell whoever has the board open.
      const hot = d.gyms.flatMap((g) => g.leads.filter((l) => l.agent?.state === "call_now" && l.status !== "done"
        && Date.now() - new Date(l.agent.at).getTime() < 30 * 60000).map((l) => [l, g]));
      if (callNow.current) {
        const freshHot = hot.filter(([l]) => !callNow.current.has(l.id));
        if (freshHot.length) {
          beep();
          freshHot.slice(0, 3).forEach(([l, g]) => toast(`Wants a call now · ${shortGym(g.name)}`, {
            description: `${l.name} asked the assistant for a call` }));
        }
      }
      callNow.current = new Set(hot.map(([l]) => l.id));
      setData(d);
    } catch (e) {
      toast.error(err(e, "Couldn't load the board"));
    }
  }, [allGyms]);

  useEffect(() => {
    load();
    const t = setInterval(() => document.visibilityState === "visible" && load(), 20000);
    return () => clearInterval(t);
  }, [load]);

  const me = data?.me;
  // Tabs: the three countries, any other country a gym is in, then All. Badge = leads not taken yet.
  const countryTabs = useMemo(() => {
    const gyms = data?.gyms || [];
    const extra = [...new Set(gyms.map((g) => g.country).filter((c) => c && !COUNTRIES.includes(c)))];
    const newIn = (list) => list.reduce((n, g) => n + g.leads.filter((l) => l.status === "new").length, 0);
    const present = new Set(gyms.map((g) => g.country || "India"));
    // A rep with their own gyms (Team): only the countries those gyms are in.
    const list = [...COUNTRIES, ...extra].filter((c) => !data?.gyms_filtered || present.has(c));
    return list.map((c) => ({ key: c, label: c, fresh: newIn(gyms.filter((g) => (g.country || "India") === c)) }))
      .concat([{ key: ALL, label: "All", fresh: newIn(gyms) }]);
  }, [data]);
  const tabOk = countryTabs.some((c) => c.key === country);
  useEffect(() => { if (data && !tabOk) setCountryState(ALL); }, [data, tabOk]);
  const gyms = useMemo(() => (data?.gyms || []).filter((g) => !tabOk || country === ALL || (g.country || "India") === country), [data, country, tabOk]);
  // The AI caller's handover column, while its pilot gym is on screen (or something is still waiting there).
  const showHandover = !!data?.ai_handover && gyms.some((g) => (data.ai_caller_gyms || []).includes(g.id)
    || data.ai_handover.some((h) => h.facility_id === g.id));
  // Held leads (just called, outcome not saved) are filed and sorted as they were before the call.
  const eff = useCallback((l) => {
    const h = held[l.id];
    return h && Date.now() - h.at < HOLD_MINUTES * 60000 ? { ...l, ...h.snap } : l;
  }, [held]);
  const counts = useMemo(() => {
    const all = gyms.flatMap((g) => g.leads).map(eff);
    return {
      new: all.filter((l) => !l.attempts && l.status !== "done" && !scheduled(l)).length,
      booked: all.filter((l) => l.owner_id && l.owner_id === me?.user_id).length,
      redials: all.filter((l) => redialDue(l) && !scheduled(l)).length,
      confirm: all.filter(confirmDue).length,
      attention: all.filter((l) => l.attention?.open || confirmDue(l)).length,
      upcoming: all.filter((l) => !!l.confirm).length,
    };
  }, [gyms, me, eff]);

  // Call = 1) lock the lead for a few minutes + WhatsApp heads-up, 2) count down so the lead can read it, 3) dial.
  // Nobody owns a lead before a consult is booked; the lock only stops two reps ringing at once.
  const phoneLabelOf = (phoneId) => (phoneId === WHATSAPP ? "WhatsApp" : (me?.phones || []).find((p) => p.id === phoneId)?.label);
  // "Call again": the second dial of a double dial (or a redial from Just called). No heads-up request, no countdown:
  // the board dials straight away and the server sends no WhatsApp.
  const callAgain = (lead, phoneId) => {
    const pid = phoneId && (phoneId === WHATSAPP || (me?.phones || []).some((p) => p.id === phoneId)) ? phoneId
      : (me?.phones || [])[0]?.id || WHATSAPP;
    hold(lead);
    const now = Date.now();
    setCalling({ lead, phoneId: pid, phoneLabel: phoneLabelOf(pid), override: !!(lockedBy(lead, me) && data?.can_manage),
      again: true, headsUp: { state: "skipped", again: true }, startedAt: now, until: now });
  };
  // One call at a time per lead: taps while a call is starting are ignored (24 Oct 2026, double taps logged 16 calls).
  const inFlight = useRef(new Set());
  const startCall = async (lead, phoneId, purpose) => {
    if (inFlight.current.has(lead.id)) return;
    if (!purpose && calledRecently(lead, me)) { callAgain(lead, phoneId); return; }
    inFlight.current.add(lead.id);
    setTimeout(() => inFlight.current.delete(lead.id), 4000);
    const phoneLabel = phoneLabelOf(phoneId);
    const override = !!(lockedBy(lead, me) && data?.can_manage);
    try {
      const res = await nurtureHeadsUp(lead.id, phoneId, override, purpose);
      hold(lead);
      load();
      const base = { lead, phoneId, phoneLabel, override, purpose, headsUp: res.heads_up, startedAt: Date.now(), again: !!res.again };
      if (res.countdown > 0) {
        setCalling({ ...base, until: Date.now() + res.countdown * 1000 });
      } else {
        setCalling({ ...base, until: Date.now() });
      }
    } catch (e) {
      toast.error(err(e, "Couldn't start the call"));
      load();
    }
  };

  const dial = async (c) => {
    const key = `dial:${c.lead.id}`;
    if (inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setTimeout(() => inFlight.current.delete(key), 15000);
    try {
      const res = await callNurtureLead(c.lead.id, c.phoneId, !c.again, c.override, c.purpose, !!c.again);
      if (res.call.mode === "exotel") toast.success(c.again ? "Calling again: your phone rings now. Pick up and Exotel connects the lead."
        : "Your phone will ring first. Pick up and Exotel connects the lead.");
      if (res.tel) window.location.href = res.tel;          // the "Dial now" button below covers browsers that block this
      setCalling({ ...c, result: res });
      load();
    } catch (e) {
      // The lead said "not now" on WhatsApp just as the countdown ended: show that instead of an error.
      const st = e?.response?.status === 409 ? await getNurtureHeadsUpStatus(c.lead.id).catch(() => null) : null;
      if (st?.declined) {
        setCalling({ ...c, reply: st, declined: true });
      } else {
        toast.error(err(e, "Couldn't place the call"));
        setCalling(null);
      }
      load();
    }
  };

  // WhatsApp reply to the heads-up (routes/nurture_reply.py), polled by the calling dialog every 2 s.
  // "Not now" / "stop" before the call is placed stops the countdown; after it, it's only shown.
  const onReply = useCallback((leadId, st) => setCalling((cur) => {
    if (!cur || cur.lead.id !== leadId) return cur;
    const fresh = freshReply(st, cur.startedAt);
    const reply = fresh || st.state === "no_scope" ? st : (cur.reply || null);
    const declined = cur.declined || (!cur.result && fresh && st.declined);
    if (reply === cur.reply && declined === cur.declined) return cur;
    return { ...cur, reply, declined };
  }), []);
  const declinedNow = !!calling?.declined;
  useEffect(() => { if (declinedNow) load(); }, [declinedNow, load]);

  return (
    <div className="space-y-4" data-testid="lead-nurture-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="min-w-0">
          <div className="eyebrow">Front End Challenge · Every gym</div>
          <h1 className="font-display text-2xl sm:text-3xl font-extrabold mt-1 tracking-tight">Lead Nurture</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl hidden sm:block">
            New leads from every gym land here within a minute. Nobody owns a lead until a consult is booked: whoever is on shift calls the due and overdue ones first. While you&apos;re on a call it shows on the card so no one else rings them. Whoever books the consult becomes the owner, here and in GoHighLevel.
          </p>
          {data?.on_shift?.length > 0 ? (
            <p className="text-xs text-emerald-400 mt-1.5" data-testid="on-shift-now">
              On shift now: {data.on_shift.map((u) => `${u.name} (till ${u.until_local}${u.covering_for ? `, covering ${firstName(u.covering_for)}` : ""})`).join(", ")}
            </p>
          ) : data && (
            <p className="text-xs text-zinc-500 mt-1.5" data-testid="on-shift-now">Nobody is on shift right now (Shifts tab).</p>
          )}
          {data?.online?.length > 0 && (
            <p className="text-[11px] text-zinc-500 mt-0.5" data-testid="online-now">
              Board open: {data.online.map((u) => u.name).join(", ")}
            </p>
          )}
          <TodayLine today={data?.today} />
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => setPhonesOpen(true)} data-testid="my-phones-btn">
            <Smartphone className="h-4 w-4 mr-2" /> My phones ({me?.phones?.length || 0})
          </Button>
          {data?.can_manage && (
            <Button variant="outline" size="sm" onClick={() => setTestOpen(true)} data-testid="test-call-btn">
              <Phone className="h-4 w-4 mr-2" /> Test call
            </Button>
          )}
          {data?.can_manage && (
            <Button variant="outline" size="sm" onClick={() => setIntakeOpen(true)} data-testid="intake-status-btn">
              Intake
            </Button>
          )}
          <Button variant="outline" size="sm" onClick={async () => { const r = await nurtureSyncNow().catch(() => null); if (r) toast(r.paused ? r.message : `${r.added} new`); load(); }}
            data-testid="sync-now-btn">
            <RefreshCw className="h-4 w-4 mr-2" /> Check now
          </Button>
        </div>
      </div>

      {data?.exotel_ready && data.exotel_error && (
        <div className="rounded-lg border border-rose-500/40 bg-rose-500/[0.08] px-4 py-2.5 text-sm text-rose-200" data-testid="exotel-error-banner">
          {data.exotel_error.message}
        </div>
      )}
      {data && !data.exotel_ready && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-2.5 text-sm text-amber-200">
          Exotel isn&apos;t connected yet, so calls can&apos;t be recorded. Direct and WhatsApp calls work and are logged.
        </div>
      )}
      {data?.gyms?.some((g) => g.appt_error) && (
        <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.07] px-4 py-2.5 text-sm text-amber-200" data-testid="appt-error-banner">
          Can&apos;t read appointments in GoHighLevel for {data.gyms.filter((g) => g.appt_error).map((g) => shortGym(g.name)).join(", ")}
          {" "}({data.gyms.find((g) => g.appt_error).appt_error}). No-shows, cancellations and confirmation calls for these gyms can&apos;t be tracked until it&apos;s fixed.
        </div>
      )}
      {data && me?.phones?.length === 0 && (
        <div className="rounded-lg border border-[#262626] bg-[#0f0f0f] px-4 py-2.5 text-sm text-zinc-300">
          Add the phones you call from under <b>My phones</b>. WhatsApp calling through GoHighLevel works without one.
        </div>
      )}

      <div className="flex gap-1 border-b border-[#1f1f1f] overflow-x-auto">
        {[["board", "Board"], ["calls", "Calls"], ["shifts", "Shifts"], ...(data?.can_manage ? [["team", "Team"], ["analytics", "Analytics"], ["assistant", "Assistant"]] : []), ["script", "Script"], ["practice", "Practice"]].map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 whitespace-nowrap ${tab === k ? "border-rose-500 text-white" : "border-transparent text-zinc-400 hover:text-zinc-200"}`}>
            {label}
          </button>
        ))}
      </div>

      {tab === "board" && (
        <>
          <div className="flex gap-2 overflow-x-auto pt-2 pb-1 -mx-1 px-1" role="tablist" aria-label="Country" data-testid="country-tabs">
            {countryTabs.map((c) => (
              <button key={c.key} role="tab" aria-selected={country === c.key} onClick={() => setCountry(c.key)}
                data-testid={`country-${c.key.replace(/\s+/g, "-").toLowerCase()}`}
                className={`relative shrink-0 rounded-lg border px-4 py-2 text-sm font-medium ${country === c.key ? "border-rose-500 bg-rose-500/10 text-white" : "border-[#262626] bg-[#0d0d0d] text-zinc-400 hover:text-zinc-200"}`}>
                {c.label}
                {c.fresh > 0 && (
                  <span className="absolute -top-2 -right-2 min-w-[20px] rounded-full bg-rose-600 px-1.5 text-[11px] leading-5 font-semibold text-white text-center shadow"
                    title={`${c.fresh} new lead${c.fresh > 1 ? "s" : ""} not called yet`}>
                    {c.fresh}
                  </span>
                )}
              </button>
            ))}
            {(data?.my_gyms?.length > 0) && (
              <label className="shrink-0 flex items-center gap-1.5 text-xs text-zinc-400 ml-1 self-center" data-testid="show-all-gyms">
                <input type="checkbox" checked={allGyms} onChange={(e) => setAllGyms(e.target.checked)} />
                Show all gyms {!allGyms && <span className="text-zinc-600">(you have {data.my_gyms.length})</span>}
              </label>
            )}
          </div>
          {data && <AiHandoffs items={data.ai_handoffs} />}
          {data && (
            <JustCalled items={data.just_called}
              onCallAgain={(lead, phoneId) => callAgain(lead, phoneId)}
              onOutcome={(call, lead) => setOutcomeFor({ call, lead })}
              onCallback={(call, lead) => setOutcomeFor({ call, lead, preset: "callback" })} />
          )}
          {data && (
            <LeadSearch gyms={data.gyms} onAdded={() => load()}
              renderLead={(l) => (
                <LeadCard lead={l} me={me} canManage={data.can_manage} exotelReady={data.exotel_ready} anyStatus notesVersion={notesVersion}
                  confirmMode={!!l.confirm?.callable && (l.status === "done" || scheduled(l))}
                  onCall={startCall} onOutcome={(call) => setOutcomeFor({ lead: l, call })} onChanged={load} />
              )} />
          )}
          <div className="flex flex-wrap items-center gap-2 text-sm">
            {[["attention", `Needs attention (${counts.attention})`], ["open", `Open`], ["new", `New (${counts.new})`], ["redials", `Redials due (${counts.redials})`],
              ["confirm", `Confirmations (${counts.confirm} due · ${counts.upcoming} upcoming)`], ["booked", `Booked by me (${counts.booked})`], ["done", "Done today"]].map(([k, label]) => (
              <button key={k} onClick={() => setView(k)} data-testid={`view-${k}`}
                className={`relative rounded-full px-3 py-1 border ${view === k ? "border-rose-500 bg-rose-500/10 text-white" : "border-[#262626] text-zinc-400"}`}>
                {label}
                {k === "attention" && counts.attention > 0 && (
                  <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-amber-400 align-middle" data-testid="attention-badge" />
                )}
                {k === "confirm" && counts.confirm > 0 && (
                  <span className="ml-1.5 inline-block h-2 w-2 rounded-full bg-sky-500 align-middle"
                    title={`${counts.confirm} confirmation call${counts.confirm > 1 ? "s" : ""} due now`} data-testid="confirm-badge" />
                )}
              </button>
            ))}
          </div>
          {!data ? (
            <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>
          ) : gyms.length === 0 ? (
            <div className="card-surface p-8 text-center text-sm text-zinc-500">No gyms in {country}.</div>
          ) : (
            // Gyms side by side, one column each; the row scrolls sideways (swipe on phones),
            // and each column scrolls down on its own.
            <HScroll count={gyms.length + (showHandover ? 1 : 0)} resetKey={country}>
            <div className="flex gap-3 overflow-x-auto pb-3 -mx-3 px-3 sm:mx-0 sm:px-0 snap-x snap-mandatory sm:snap-none overscroll-x-contain"
              data-testid="gym-columns">
              {showHandover && (
                <AiHandoverColumn onChanged={load}
                  items={data.ai_handover.filter((h) => gyms.some((g) => g.id === h.facility_id))} />
              )}
              {gyms.map((g) => {
                const live = Object.fromEntries(g.leads.map((l) => [l.id, l]));
                const leads = g.leads.map(eff).filter((l) => (
                  view === "attention" ? !!l.attention?.open || confirmDue(l)
                  : view === "open" ? l.status !== "done" && !scheduled(l)
                    : view === "new" ? !l.attempts && l.status !== "done" && !scheduled(l)
                      : view === "redials" ? redialDue(l) && !scheduled(l)
                      : view === "confirm" ? !!l.confirm
                        : view === "booked" ? l.owner_id && l.owner_id === me.user_id
                          : l.done_today));
                if (view === "redials") leads.sort((a, b) => dueAt(a) - dueAt(b));
                if (view === "open") leads.sort((a, b) => (!!waitingUntil(a) - !!waitingUntil(b))
                  || (waitingUntil(a) && waitingUntil(b) ? waitingUntil(a) - waitingUntil(b) : 0));
                // Filed / sorted on the held snapshot, shown live.
                for (let i = 0; i < leads.length; i += 1) leads[i] = live[leads[i].id] || leads[i];
                if (view === "confirm") leads.sort((a, b) => new Date(a.confirm.appt_at) - new Date(b.confirm.appt_at));
                const fresh = g.leads.filter((l) => l.status === "new").length;
                return (
                  <section key={g.id} className="card-surface shrink-0 w-[86vw] max-w-[320px] sm:w-[320px] snap-start flex flex-col min-w-0"
                    data-testid={`gym-${g.id}`}>
                    <div className="flex items-center justify-between gap-2 px-3 pt-3 pb-2 border-b border-[#1a1a1a]">
                      <div className="min-w-0">
                        <div className="font-semibold text-sm truncate" title={g.name}>{shortGym(g.name)}</div>
                        {country === ALL && <div className="text-[10px] uppercase tracking-wider text-zinc-600">{g.country}</div>}
                        {view === "confirm" && g.consult_hours?.message && (
                          <div className="text-[10px] text-amber-400/80" title={g.consult_hours.reason}>{g.consult_hours.message}</div>
                        )}
                      </div>
                      <span className={`text-xs shrink-0 ${g.connected && fresh ? "text-rose-300" : "text-zinc-500"}`}>
                        {g.connected ? `${fresh} new` : "Not connected"}
                      </span>
                    </div>
                    <div className="p-2 space-y-2 overflow-y-auto max-h-[calc(100dvh-300px)] min-h-[160px]">
                      {leads.length === 0 && <div className="text-xs text-zinc-600 px-1 py-3">Nothing here</div>}
                      {view === "confirm" && g.calendar_sync?.state === "error" && (
                        <div className="text-[10px] text-amber-400/80 px-1" title={g.calendar_sync.message}>Calendar not read: {g.calendar_sync.message}</div>
                      )}
                      {(view === "confirm" ? confirmGroups(leads) : [{ key: "all", leads }]).map((grp) => (
                        <div key={grp.key} className="space-y-2">
                          {grp.label && (
                            <div className="sticky top-0 z-[1] bg-[#0a0a0a]/95 px-1 pt-1 text-[10px] uppercase tracking-wider text-zinc-500" data-testid="confirm-group">
                              {grp.label} <span className="text-zinc-600">({grp.leads.length})</span>
                            </div>
                          )}
                          {grp.leads.map((l) => (
                            <div key={l.id} className="space-y-1">
                            {view === "attention" && !l.attention?.open && confirmDue(l) && (
                              <div className="rounded-md border border-sky-500/40 bg-sky-500/10 px-2 py-1.5 text-xs text-sky-200" data-testid="attention-confirm">
                                Confirmation call due: consult {consultWhen(l.confirm.appt_at, l.confirm.tz)}
                              </div>
                            )}
                            {view === "attention" && l.attention?.open && (
                              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-1.5 text-xs text-amber-200" data-testid="attention-reason">
                                <div>{l.attention.reason}</div>
                                <button type="button" className="mt-1 text-[11px] text-amber-300 underline"
                                  onClick={() => nurtureResolveAttention(l.id).then(load).catch(() => toast.error("Couldn't mark it done"))}>
                                  Done, sorted</button>
                              </div>
                            )}
                            <LeadCard lead={l} me={me} canManage={data.can_manage} exotelReady={data.exotel_ready} notesVersion={notesVersion}
                              confirmMode={!!l.confirm?.callable && (view === "confirm" || view === "attention" || l.status === "done" || scheduled(l))}
                              onCall={startCall} onOutcome={(call) => setOutcomeFor({ lead: l, call })} onChanged={load} />
                            </div>
                          ))}
                        </div>
                      ))}
                    </div>
                  </section>
                );
              })}
            </div>
            </HScroll>
          )}
        </>
      )}

      {tab === "calls" && <CallsTab canManage={data?.can_manage} rep={data?.can_manage ? callsRep : null} onClearRep={() => setCallsRep(null)} />}
      {tab === "shifts" && <ShiftsTab canManage={data?.can_manage_team} />}
      {tab === "team" && data?.can_manage && (
        <div className="space-y-4">
          <GhlBotWarning warnings={data?.agent_bot_warnings} />
          <TeamToday />
          <TeamManage />
          <TeamTab />
        </div>
      )}
      {tab === "analytics" && data?.can_manage && (
        <NurtureAnalytics initialCountry={country} onPickRep={(id, name) => { setCallsRep({ user_id: id, name }); setTab("calls"); }} />
      )}
      {tab === "assistant" && data?.can_manage && <div className="space-y-4"><AiCallerPanel /><AssistantTab /></div>}
      {tab === "script" && <ScriptTab canManage={data?.can_manage} gyms={data?.gyms || []} />}
      {tab === "practice" && <Navigate to="/improvement?tab=lead-calls" replace />}

      <TestCallDialog open={testOpen} onOpenChange={setTestOpen} phones={me?.phones} onPlaced={() => setTab("calls")} />
      <PhonesDialog open={phonesOpen} onOpenChange={setPhonesOpen} phones={me?.phones || []} onSaved={load} />
      <OutcomeDialog value={outcomeFor} onClose={() => setOutcomeFor(null)}
        onSaved={(leadId) => { release(leadId); setNotesVersion((v) => v + 1); load(); }}
        onCallAgain={(lead, phoneId) => { setOutcomeFor(null); callAgain(lead, phoneId); }} />
      <IntakeStatusDialog open={intakeOpen} onOpenChange={setIntakeOpen} />
      <CallingDialog value={calling} onDial={dial} confirmScript={data?.confirm_script} onReply={onReply} canManage={!!data?.can_manage}
        onAgain={(c) => callAgain(c.lead, c.phoneId)}
        onBooked={() => { if (calling) release(calling.lead.id); setCalling(null); load(); }}
        onDone={(c) => { setCalling(null); setOutcomeFor({ call: c.result.call, lead: c.lead }); }}
        onClose={() => { setCalling(null); load(); }} />
    </div>
  );
}

const COLUMN_STEP = 332;   // a gym column (320px) plus the gap

// Arrow buttons and a slider above the side-by-side gym columns, so the row can be moved without
// a trackpad. Hidden when every gym already fits on screen.
function HScroll({ children, count, resetKey }) {
  const wrap = useRef(null);
  const [pos, setPos] = useState({ left: 0, max: 0, width: 0 });
  const row = () => wrap.current?.querySelector('[data-testid="gym-columns"]');
  const measure = useCallback(() => {
    const el = row();
    if (el) setPos({ left: el.scrollLeft, max: Math.max(0, el.scrollWidth - el.clientWidth), width: el.clientWidth });
  }, []);
  useEffect(() => {
    const el = row();
    if (!el) return undefined;
    measure();
    el.addEventListener("scroll", measure, { passive: true });
    const ro = typeof ResizeObserver !== "undefined" ? new ResizeObserver(measure) : null;
    ro?.observe(el);
    window.addEventListener("resize", measure);
    return () => { el.removeEventListener("scroll", measure); ro?.disconnect(); window.removeEventListener("resize", measure); };
  }, [measure, count]);
  useEffect(() => { const el = row(); if (el) el.scrollTo({ left: 0 }); }, [resetKey]);
  const go = (dx) => row()?.scrollBy({ left: dx, behavior: "smooth" });
  const first = Math.min(count, Math.floor(pos.left / COLUMN_STEP + 0.5) + 1);
  const last = Math.min(count, Math.floor((pos.left + pos.width) / COLUMN_STEP + 0.15));
  const btn = "h-8 w-8 shrink-0 grid place-items-center rounded-full border border-[#262626] bg-[#111] text-zinc-300 hover:text-white hover:bg-[#1a1a1a] disabled:opacity-30 disabled:hover:bg-[#111]";
  return (
    <div ref={wrap}>
      {pos.max > 4 && (
        <div className="flex items-center gap-3 mb-2" data-testid="gym-slider">
          <button type="button" className={btn} onClick={() => go(-COLUMN_STEP)} disabled={pos.left <= 2} aria-label="Previous gym">
            <ChevronLeft className="h-4 w-4" />
          </button>
          <input type="range" min={0} max={pos.max} step={1} value={Math.min(pos.left, pos.max)} aria-label="Scroll the gyms"
            onChange={(e) => { const el = row(); if (el) el.scrollLeft = Number(e.target.value); }}
            className="flex-1 h-1.5 cursor-pointer accent-rose-500" />
          <button type="button" className={btn} onClick={() => go(COLUMN_STEP)} disabled={pos.left >= pos.max - 2} aria-label="Next gym">
            <ChevronRight className="h-4 w-4" />
          </button>
          <span className="text-[11px] text-zinc-500 shrink-0 tabular-nums hidden sm:inline">
            {first === last ? `Gym ${first}` : `Showing ${first}–${Math.max(first, last)}`} of {count}
          </span>
        </div>
      )}
      {children}
    </div>
  );
}

function LeadCard({ lead, me, canManage, exotelReady, confirmMode, onCall, onOutcome, onChanged, anyStatus, notesVersion }) {
  const purpose = confirmMode ? "confirm" : undefined;
  const callable = lead.status !== "done" || confirmMode || anyStatus;     // search results: any lead can be called back
  const [notesOpen, setNotesOpen] = useState(false);
  const noteCount = (lead.fpa_notes_count || 0) + (lead.ghl_notes_count || 0);
  const mine = !!lead.owner_id && lead.owner_id === me.user_id;      // booked the consult
  const lock = lockedBy(lead, me);
  const blockedByLock = !!lock && !canManage;
  const calledByMe = lead.last_caller_id === me.user_id || lead.active_call?.user_id === me.user_id;
  const phones = me.phones || [];
  const indian = (lead.phone || "").startsWith("+91");
  // A Voiger call that connected: the rep it was matched to logs what happened (managers too when no rep matched).
  const voigerAsk = !!lead.voiger_outcome && (lead.voiger_outcome.user_id === me.user_id || (canManage && !lead.voiger_outcome.user_id));
  return (
    <div className={`rounded-lg border px-3 py-2.5 ${lead.status === "new" ? "border-rose-500/40 bg-rose-500/[0.04]"
      : confirmMode && lead.confirm?.state === "due" ? "border-sky-500/40 bg-sky-500/[0.04]" : "border-[#222] bg-[#0d0d0d]"}`}
      data-testid={`lead-${lead.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="font-medium text-sm truncate">{lead.name}</div>
          <div className="text-[11px] text-zinc-500 truncate">
            {cameIn(lead)}{lead.area ? ` · ${lead.area}` : ""}{lead.source ? ` · ${lead.source}` : ""}
          </div>
          {lead.campaign && <div className="text-[11px] text-zinc-500 truncate" title={lead.campaign}>Ad: {lead.campaign}</div>}
        </div>
        {lead.attempts > 0 && <span className="text-[10px] text-zinc-500 shrink-0">{lead.attempts} call{lead.attempts > 1 ? "s" : ""}</span>}
      </div>
      <EnquiredAgain lead={lead} />
      <ConfirmLine lead={lead} me={me} />
      <RedialChips lead={lead} />
      {lock && (
        <div className="mt-1.5 text-[11px] text-sky-300 flex items-center gap-1" data-testid={`calling-${lead.id}`}>
          <Phone className="h-3 w-3" /> {firstName(lock.name)} is calling…
        </div>
      )}
      {lead.owner_name && (
        <div className={`mt-1.5 text-[11px] flex items-center gap-1 ${mine ? "text-emerald-400" : "text-zinc-400"}`}>
          <UserCheck className="h-3 w-3" /> Booked by {mine ? "you" : firstName(lead.owner_name)}
          <ApptLine lead={lead} />
        </div>
      )}
      {lead.appt_sync?.state === "error" && lead.owner_id && (
        <div className="mt-1 text-[10px] text-amber-400/80" title={lead.appt_sync.message}>Appointment not checked: {lead.appt_sync.message}</div>
      )}
      {!lead.owner_name && lead.last_outcome && lead.last_outcome !== "no_show" && lead.last_caller_name && (
        <div className="mt-1.5 text-[11px] text-zinc-500">{OUTCOME_LABEL[lead.last_outcome]} · {calledByMe ? "you" : firstName(lead.last_caller_name)} called {ago(lead.last_called_at)}</div>
      )}
      {mine && lead.owner_sync?.state && lead.owner_sync.state !== "assigned" && (
        <div className="mt-1 text-[10px] text-amber-400/80" title={lead.owner_sync.message}>GoHighLevel owner not set: {lead.owner_sync.message}</div>
      )}
      {lead.callback_at && lead.last_outcome === "callback" && (
        <div className={`mt-1 text-[11px] ${callbackDue(lead) ? "text-amber-300" : "text-sky-300"}`} data-testid={`callback-${lead.id}`}>
          Next call {new Date(lead.callback_at).toLocaleString([], { dateStyle: "medium", timeStyle: "short" })}{callbackDue(lead) ? " · due now" : ""}
        </div>
      )}
      {lead.hot_callback && lead.last_outcome === "callback" && (
        <div className="mt-1 text-[11px] text-amber-300" data-testid={`hot-callback-${lead.id}`}>
          Rang us back on Voiger {ago(lead.hot_callback.at)} (missed) · call now
        </div>
      )}
      <WaReplyLine lead={lead} onChanged={onChanged} />
      <AgentLine lead={lead} onChanged={onChanged} />
      {callable && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-7 px-2.5 text-xs" data-testid={`call-${lead.id}`} disabled={!lead.phone || blockedByLock}
                title={lock ? (canManage ? `${firstName(lock.name)} is on a call with them. As a manager you can still call.` : `${firstName(lock.name)} is calling them right now`) : undefined}>
                <Phone className="h-3.5 w-3.5 mr-1" /> {confirmMode ? "Confirm" : "Call"} <ChevronDown className="h-3 w-3 ml-1" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="bg-[#0f0f0f] border-[#262626] text-zinc-200 w-64">
              <DropdownMenuLabel className="text-xs text-zinc-500">{lead.phone}</DropdownMenuLabel>
              {phones.map((p) => {
                const blocked = p.mode === "exotel" && (!exotelReady || !indian);
                return (
                  <DropdownMenuItem key={p.id} disabled={blocked} onClick={() => onCall(lead, p.id, purpose)} className="cursor-pointer focus:bg-[#1a1a1a]">
                    {p.mode === "exotel" || p.mode === "voiger" ? <Mic className="h-4 w-4 mr-2 text-emerald-400" /> : <MicOff className="h-4 w-4 mr-2 text-zinc-500" />}
                    <div className="min-w-0">
                      <div className="text-sm">{p.label}</div>
                      <div className="text-[10px] text-zinc-500">
                        {p.mode === "exotel" ? (blocked ? (!exotelReady ? "Exotel not connected" : "Exotel: Indian numbers only") : "Via Exotel · recorded")
                          : p.mode === "voiger" ? "Dial in Voiger · recorded" : "Dial directly · not recorded"}
                      </div>
                    </div>
                  </DropdownMenuItem>
                );
              })}
              <DropdownMenuSeparator className="bg-[#262626]" />
              <DropdownMenuItem onClick={() => onCall(lead, WHATSAPP, purpose)} className="cursor-pointer focus:bg-[#1a1a1a]">
                <MessageCircle className="h-4 w-4 mr-2 text-green-400" />
                <div>
                  <div className="text-sm">WhatsApp call</div>
                  <div className="text-[10px] text-zinc-500">Opens the lead in GoHighLevel · not recorded</div>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {voigerAsk && (
            <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs border-sky-500/50 text-sky-200" data-testid={`voiger-outcome-${lead.id}`}
              onClick={() => onOutcome({ id: lead.voiger_outcome.call_id, mode: "voiger" })}>Voiger call · add outcome</Button>
          )}
          {calledByMe && !voigerAsk && (
            <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={() => onOutcome()}>Log outcome</Button>
          )}
          <Button size="sm" variant="ghost" className="h-7 px-2 text-xs text-zinc-400" onClick={() => setNotesOpen((v) => !v)}
            data-testid={`notes-toggle-${lead.id}`}>
            Notes{noteCount ? ` (${noteCount})` : ""}
          </Button>
        </div>
      )}
      {!callable && (
        <button className="mt-1.5 text-[11px] text-zinc-500 underline" onClick={() => setNotesOpen((v) => !v)}>Notes{noteCount ? ` (${noteCount})` : ""}</button>
      )}
      {lead.status === "done" && lead.left_board?.reason && (
        <div className="mt-1 text-[10px] text-zinc-500">Off the board: {lead.left_board.reason}</div>
      )}
      {notesOpen && <div className="mt-2"><LeadNotes leadId={lead.id} version={notesVersion} /></div>}
    </div>
  );
}

// Confirmation call before the consult: "Consult today 6:30 PM · confirm now", retry time, unconfirmed, confirmed by …
const CONFIRM_TEXT = {
  due: "confirm now", later: "confirm from", retry_later: "no answer · try again", unconfirmed: "unconfirmed",
  confirmed: "confirmed", confirmed_at_booking: "confirmed at booking", rescheduling: "rescheduling",
};
function ConfirmLine({ lead, me }) {
  const c = lead.confirm;
  if (!c) return null;
  const tone = c.state === "due" ? "text-sky-300" : c.state === "unconfirmed" ? "text-amber-300"
    : c.state === "confirmed" || c.state === "confirmed_at_booking" ? "text-emerald-300" : "text-zinc-400";
  const by = c.reached_by_name ? ` by ${c.reached_by_name === me?.name ? "you" : firstName(c.reached_by_name)}` : "";
  const extra = c.state === "later" ? ` ${gymTime(c.opens_at, c.tz)}`
    : c.state === "retry_later" ? ` ${gymTime(c.retry_at, c.tz)}`
      : c.state === "confirmed" || c.state === "rescheduling" ? by : "";
  return (
    <div className="mt-1.5 text-[11px]" data-testid={`confirm-${lead.id}`}>
      <div className={`flex items-center gap-1 ${tone}`}>
        <CalendarCheck className="h-3 w-3 shrink-0" />
        <span>Consult {consultWhen(c.appt_at, c.tz)} · {CONFIRM_TEXT[c.state] || c.state}{extra}</span>
      </div>
      <div className="text-[10px] text-zinc-500">
        {c.booked_by_name || lead.booked_by_name ? `Booked by ${firstName(c.booked_by_name || lead.booked_by_name)}`
          : c.booked_via === "calendar" ? "Booked in GoHighLevel" : "Booked by —"}
        {lead.lead_kind === "calendar" ? " · on the calendar, not in the lead pipeline" : ""}
        {c.attempts > 0 && c.state !== "confirmed" ? ` · ${c.attempts} no answer${c.attempts > 1 ? "s" : ""}` : ""}
        {c.state === "unconfirmed" && c.unconfirmed_reason ? ` · ${c.unconfirmed_reason}` : ""}
        {c.moved_from ? ` · moved from ${consultWhen(c.moved_from, c.tz)}` : ""}
        {c.stage === "day_before" ? " · 24-hour call" : c.stage === "day_of" ? " · same-day call, 4 to 1 hours before" : ""}
      </div>
      {c.outside_hours && <div className="text-[10px] text-amber-400/80" title="Not inside the gym's consult hours in GoHighLevel">Outside consult hours</div>}
    </div>
  );
}

// Booked lead: where its GoHighLevel appointment stands.
function ApptLine({ lead }) {
  const w = lead.appt_watch;
  const b = (lead.bookings || [])[(lead.bookings || []).length - 1];
  const tz = lead.redial?.tz;
  if (!w) return null;
  const when = b?.appt_at ? ` for ${apptTime(b.appt_at, tz)}` : "";
  const text = w.state === "showed" ? `· showed up${when}`
    : w.state === "cancelled" ? `· cancelled${when}`
      : w.state === "watching" ? (b?.appt_at ? `· consult${when}` : "· waiting for the appointment in GoHighLevel")
        : when ? `·${when}` : "";
  return <span className={w.state === "showed" ? "text-emerald-300" : w.state === "cancelled" ? "text-amber-300" : "text-zinc-500"}>{text}</span>;
}

function RedialChips({ lead }) {
  const r = lead.redial;
  const chip = "inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] leading-4 whitespace-nowrap";
  const tone = r?.due === "overdue" ? "border-rose-500/50 bg-rose-500/10 text-rose-300"
    : r?.due === "due" ? "border-amber-500/50 bg-amber-500/10 text-amber-300" : "border-[#2a2a2a] text-zinc-400";
  const when = r?.due === "overdue" ? "overdue" : r?.due === "due" ? "due now" : `next call ${gymTime(r?.next_at, r?.tz)}`;
  const sync = lead.stage_sync;
  return (
    <>
      {(r?.kind || lead.stage_name) && (
        <div className="mt-1.5 flex flex-wrap gap-1">
          {r?.kind === "four_week_callback" && (
            <span className={`${chip} ${r.due === "later" ? "border-violet-500/40 text-violet-300" : tone}`} data-testid={`redial-${lead.id}`}
              title="Sat 4 weeks in 4 Week Follow Up: one more call before GoHighLevel's 4-week message">
              4-week callback · {r.due === "later" ? gymTime(r.next_at, r.tz) : when}
            </span>
          )}
          {r?.kind === "reschedule" && (
            <span className={`${chip} ${r.due === "later" ? "border-orange-500/40 text-orange-300" : tone}`} data-testid={`redial-${lead.id}`}
              title="No-show: 3 calls to rebook (within 2 hours, next day, day 3), then 4 Week Follow Up">
              No-show · rebook attempt {r.attempt} of {r.of} · {when}
            </span>
          )}
          {r?.kind === "cadence" && (
            <span className={`${chip} ${tone}`} data-testid={`redial-${lead.id}`}
              title={r.attempt === 1 ? "First call: within 10 minutes of the lead landing (double-dial)" : "Redial standard: 6 attempts over 7 days, 9 am–8 pm"}>
              Attempt {r.attempt} of {r.of} · {when}
            </span>
          )}
          {lead.stage_name && <span className={`${chip} border-[#2a2a2a] text-zinc-500`} title="Stage in GoHighLevel">{lead.stage_name}</span>}
        </div>
      )}
      <NoShowLine lead={lead} />
      {sync?.state === "error" && (
        <div className="mt-1 text-[10px] text-amber-400/80" title={sync.message}>Couldn&apos;t move to {sync.stage} in GoHighLevel: {sync.message}</div>
      )}
      {!r?.kind && r?.state === "stopped" && r.reason?.includes("GoHighLevel") && lead.status !== "done" && (
        <div className="mt-1 text-[10px] text-zinc-500">Redials stopped: {r.reason}</div>
      )}
    </>
  );
}

// "No-show · was booked by Indu for Mon 12 Oct 6:30 PM" — the latest bookings that didn't happen.
function NoShowLine({ lead }) {
  if (lead.owner_id) return null;
  const missed = (lead.bookings || []).filter((b) => b.result === "noshow" || b.result === "cancelled");
  if (!missed.length) return null;
  const b = missed[missed.length - 1];
  const label = b.result === "noshow" ? "No-show" : "Cancelled";
  return (
    <div className="mt-1 text-[11px] text-orange-300/90" data-testid={`noshow-${lead.id}`}
      title={missed.length > 1 ? `${missed.length} bookings didn't happen` : undefined}>
      {label} · was booked by {firstName(b.booked_by_name)}{b.appt_at ? ` for ${apptTime(b.appt_at, lead.redial?.tz)}` : ""}
      {missed.length > 1 ? ` (${missed.length}×)` : ""}
    </div>
  );
}

// The lead's WhatsApp reply to the heads-up, on the card (routes/nurture_reply.py): "Lead chose: Tomorrow 10:00 (replied on WhatsApp)".
function WaReplyLine({ lead, onChanged }) {
  const w = lead.wa_reply;
  const [busy, setBusy] = useState(false);
  if (!w || !w.at || Date.now() - new Date(w.at).getTime() > 3 * 86400000) return null;
  const quote = w.text ? `“${w.text.length > 90 ? `${w.text.slice(0, 90)}…` : w.text}”` : "";
  if (w.intent === "ok") return <div className="mt-1 text-[11px] text-emerald-300/90" data-testid={`wa-reply-${lead.id}`}>WhatsApp: {quote}</div>;
  if (w.intent === "unclear") return <div className="mt-1 text-[11px] text-zinc-400" data-testid={`wa-reply-${lead.id}`}>WhatsApp reply: {quote}</div>;
  if (w.intent === "stop") {
    const label = OUTCOME_LABEL[w.suggested_outcome] || "Not interested";
    const mark = async () => {
      setBusy(true);
      try {
        await resolveNurtureHeadsUp(lead.id, { outcome: w.suggested_outcome || "not_interested" });
        toast.success(`Marked ${label.toLowerCase()}`);
        onChanged?.();
      } catch (e) {
        toast.error(err(e, "Couldn't save"));
      } finally {
        setBusy(false);
      }
    };
    return (
      <div className="mt-1.5 rounded-md border border-amber-500/30 bg-amber-500/[0.06] px-2 py-1.5 text-[11px] text-amber-200" data-testid={`wa-reply-${lead.id}`}>
        Asked on WhatsApp not to be called: {quote}
        {!w.resolved && lead.status !== "done" && (
          <div className="mt-1 flex items-center gap-2">
            <span className="text-amber-300/80">Suggested: {label}</span>
            <button onClick={mark} disabled={busy} className="rounded border border-amber-500/40 px-1.5 py-0.5 hover:bg-amber-500/10" data-testid={`wa-mark-${lead.id}`}>
              Mark {label.toLowerCase()}
            </button>
          </div>
        )}
      </div>
    );
  }
  const chosen = w.callback_source === "choice" || w.callback_source === "time";
  return (
    <div className="mt-1 text-[11px] text-sky-300/90" data-testid={`wa-reply-${lead.id}`}>
      {chosen ? `Lead chose: ${w.callback_label} (replied on WhatsApp)` : `Declined on WhatsApp: ${quote}`}
      {chosen && w.outside_hours && <span className="text-amber-300/90"> · outside calling hours</span>}
      {!chosen && w.followup === "sent" && <span className="text-zinc-500"> · asked when to call back</span>}
      {!chosen && (w.followup === "no_scope" || w.followup === "error") && <span className="text-amber-300/90"> · ask them when to call back</span>}
    </div>
  );
}

// What the lead said on WhatsApp, as a line in the calling dialog.
function ReplyNote({ reply, first, inCall }) {
  if (!reply) return null;
  if (reply.state === "no_scope" && !reply.reply_text) {
    return <p className="text-[11px] text-zinc-500" data-testid="reply-no-scope">Can&apos;t see WhatsApp replies for this gym ({reply.message}).</p>;
  }
  const text = reply.reply_text || reply.unclear_text;
  if (!text) return null;
  if (reply.intent === "ok") {
    return <div className="rounded-md border border-emerald-500/30 bg-emerald-500/[0.06] px-3 py-2 text-sm text-emerald-200" data-testid="reply-banner">{first} replied: “{text}”</div>;
  }
  if (["not_now", "time", "stop"].includes(reply.intent)) {
    return (
      <div className="rounded-md border border-amber-500/40 bg-amber-500/[0.08] px-3 py-2 text-sm text-amber-100" data-testid="reply-banner">
        <div><b>{first} replied on WhatsApp:</b> “{text}”</div>
        {inCall && (
          <div className="text-[11px] text-amber-200/80 mt-1">
            They may not pick up. A call already ringing can&apos;t be cancelled from here; if it goes unanswered it&apos;s saved as Call back later, not a missed call.
          </div>
        )}
        {reply.callback_label && (reply.callback_source === "choice" || reply.callback_source === "time") && (
          <div className="text-[11px] text-sky-200 mt-1">Lead chose: {reply.callback_label} (replied on WhatsApp)</div>
        )}
      </div>
    );
  }
  return <p className="text-xs text-zinc-400" data-testid="reply-banner">{first} replied: “{text}”</p>;
}

function CallingDialog({ value, onDial, onDone, onClose, onReply, canManage, confirmScript, onBooked, onAgain }) {
  const [now, setNow] = useState(Date.now());
  const [when, setWhen] = useState("");
  const [picking, setPicking] = useState(false);
  const [busy, setBusy] = useState(false);
  const fired = useRef(null);
  const declined = !!value?.declined;
  useEffect(() => {
    if (!value || value.result || declined) return undefined;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [value, declined]);
  // Has the lead replied on WhatsApp? Polled every 2 s while the dialog is open (countdown, call, cancelled pop-up).
  const leadId = value?.lead?.id;
  const watch = !!value && value.purpose !== "confirm";
  useEffect(() => {
    if (!watch || !leadId) return undefined;
    let alive = true;
    const tick = () => getNurtureHeadsUpStatus(leadId).then((st) => { if (alive) onReply(leadId, st); }).catch(() => {});
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [watch, leadId, onReply]);
  useEffect(() => { setPicking(false); setWhen(""); }, [leadId]);
  const left = value && !value.result && !declined ? Math.max(0, Math.ceil((value.until - now) / 1000)) : 0;
  useEffect(() => {                                     // dial once when the countdown reaches 0
    if (value && !value.result && !declined && left === 0 && fired.current !== value.until) {
      fired.current = value.until;
      onDial(value);
    }
  }, [value, left, onDial, declined]);
  if (!value) return null;
  const res = value.result;
  const sent = value.headsUp?.state === "sent";
  const first = (value.lead.name || "").split(" ")[0];
  const r = value.reply || {};
  const closable = !!res || declined;

  if (declined) {
    const stop = r.intent === "stop";
    const chosen = r.callback_source === "choice" || r.callback_source === "time";
    const resolve = async (payload, msg) => {
      setBusy(true);
      try {
        await resolveNurtureHeadsUp(value.lead.id, payload);
        toast.success(msg);
        onClose();
      } catch (e) {
        toast.error(err(e, "Couldn't save"));
      } finally {
        setBusy(false);
      }
    };
    return (
      <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
        <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-sm" data-testid="declined-dialog">
          <DialogHeader>
            <DialogTitle>{stop ? `${first} doesn't want a call` : `${first} said not now`}</DialogTitle>
            <DialogDescription>
              {value.lead.name} replied: “{r.reply_text}” — call cancelled.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-1.5 text-sm" data-testid="declined-followup">
            {stop ? (
              <p className="text-amber-200">Not closed automatically. Suggested outcome: <b>{r.suggested_label || "Not interested"}</b>.</p>
            ) : chosen ? (
              <p className="text-emerald-300">Lead chose: {r.callback_label} (replied on WhatsApp). It&apos;s on the board as a callback.
                {r.outside_hours && <span className="text-amber-300"> They asked for a time outside calling hours, so it&apos;s the nearest time we call.</span>}</p>
            ) : r.followup?.state === "sent" || r.followup?.state === "sending" ? (
              <p className="text-zinc-300">FPA asked {first} on WhatsApp when to call back. Waiting for their answer…</p>
            ) : (
              <p className="text-amber-200">Ask them when to call back{r.followup?.message ? ` (${r.followup.message})` : ""}.</p>
            )}
            {!stop && !chosen && r.callback_at && (
              <p className="text-[11px] text-zinc-500">Saved as Call back later, {gymTime(r.callback_at, r.tz)} for now. Not counted as a missed call.</p>
            )}
          </div>
          {picking && (
            <div className="flex gap-2">
              <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Call back at" data-testid="declined-when" />
              <Button size="sm" disabled={!when || busy} data-testid="declined-save"
                onClick={() => resolve({ outcome: "callback", callback_at: new Date(when).toISOString() }, "Callback saved")}>Save</Button>
            </div>
          )}
          <DialogFooter className="gap-2 sm:gap-0">
            {stop ? (
              <Button variant="outline" disabled={busy} data-testid="declined-mark"
                onClick={() => resolve({ outcome: r.suggested_outcome || "not_interested" }, `Marked ${(r.suggested_label || "Not interested").toLowerCase()}`)}>
                Mark {(r.suggested_label || "Not interested").toLowerCase()}
              </Button>
            ) : !picking && (
              <Button variant="outline" data-testid="declined-set-callback" onClick={() => { setWhen(localInput(r.callback_at)); setPicking(true); }}>Set callback</Button>
            )}
            {canManage && (
              <Button variant="outline" data-testid="declined-call-anyway"
                onClick={() => { fired.current = value.until; onDial({ ...value, override: true, declined: false }); }}>Call anyway</Button>
            )}
            <Button onClick={onClose} data-testid="declined-close">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    // No way out during the countdown: the lead has just been told a call is coming.
    <Dialog open onOpenChange={(o) => { if (!o && closable) onDone(value); }}>
      <DialogContent className={`bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-lg max-h-[92dvh] overflow-y-auto ${closable ? "" : "[&>button:last-child]:hidden"}`}
        onEscapeKeyDown={(e) => { if (!closable) e.preventDefault(); }} onPointerDownOutside={(e) => { if (!closable) e.preventDefault(); }}
        data-testid="calling-dialog">
        <DialogHeader>
          <DialogTitle data-testid="calling-title">
            {res ? `Calling ${value.lead.name}${value.again ? " again" : ""}` : value.again ? `Calling ${first} again…` : `Calling ${first} in ${left}s`}
          </DialogTitle>
          <DialogDescription>
            {value.purpose === "confirm" ? `Confirmation call for ${first}'s consult ${consultWhen(value.lead.confirm?.appt_at, value.lead.confirm?.tz)}. No WhatsApp from here: GoHighLevel sends the reminders.`
              : value.again ? `Second call, straight away: no WhatsApp heads-up and no countdown (${first} already got one).`
              : sent ? `WhatsApp sent to ${first}: you'll call in a couple of minutes, so they're expecting it.`
              : value.headsUp?.state === "skipped" ? `${first} already got a WhatsApp heads-up in the last few hours.`
                : `No WhatsApp heads-up went out (${value.headsUp?.message || "not set up for this gym"}).`}
          </DialogDescription>
        </DialogHeader>
        {!res && !value.again && (
          <div className="h-1.5 rounded bg-[#1f1f1f] overflow-hidden">
            <div className="h-full bg-rose-500 transition-all" style={{ width: `${(left / 20) * 100}%` }} />
          </div>
        )}
        <ReplyNote reply={value.reply} first={first} inCall={!!res} />
        <LeadNotes leadId={value.lead.id} prominent max={5} />
        {value.purpose === "confirm" && (
          <div className="rounded-md border border-sky-500/30 bg-sky-500/[0.06] px-3 py-2 text-sm text-zinc-200 whitespace-pre-line" data-testid="confirm-script">
            <div className="text-[10px] uppercase tracking-wider text-sky-300 mb-1">Script · Confirmation call</div>
            {confirmScript}
          </div>
        )}
        {res?.call?.mode === "exotel" && <p className="text-sm text-zinc-300">Your phone ({value.phoneLabel}) rings first. Pick up and Exotel connects {first}.</p>}
        {res?.voiger && <VoigerDial voiger={res.voiger} first={first} />}
        {res?.tel && (
          <a href={res.tel} className="block text-center rounded-md bg-rose-600 hover:bg-rose-500 py-3 font-semibold" data-testid="dial-now">
            Dial {res.call.lead_phone} now
          </a>
        )}
        {res?.open_url && (
          <a href={res.open_url} target="_blank" rel="noopener noreferrer" data-testid="open-whatsapp"
            className="block text-center rounded-md bg-green-600 hover:bg-green-500 py-3 font-semibold">
            Open {first} in GoHighLevel to WhatsApp call
          </a>
        )}
        {/* The gym's consult calendar: book a slot on the call (not for confirmation calls: they're booked already). */}
        {value.purpose !== "confirm" && !value.lead.scheduled && (
          <NurtureCalendar lead={value.lead} callId={res?.call?.id} onBooked={() => onBooked?.()} compact />
        )}
        <DialogFooter>
          {!res ? (
            <Button onClick={() => { fired.current = value.until; onDial(value); }} disabled={value.again} data-testid="call-now">
              {value.again ? "Calling again…" : "Call now"}
            </Button>
          ) : (
            <>
              {value.purpose !== "confirm" && (
                <Button variant="outline" onClick={() => onAgain(value)} data-testid="call-again">No answer · call again</Button>
              )}
              <Button onClick={() => onDone(value)} data-testid="call-finished">Done, log the outcome</Button>
            </>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TestCallDialog({ open, onOpenChange, phones: allPhones, onPlaced }) {
  const phones = useMemo(() => (allPhones || []).filter((p) => p.mode === "exotel"), [allPhones]);
  const [phoneId, setPhoneId] = useState("");
  const [to, setTo] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (open) { setPhoneId(phones[0]?.id || ""); setTo(""); } }, [open, phones]);
  const place = async () => {
    setBusy(true);
    try {
      const r = await nurtureTestCall(phoneId, to);
      toast.success(`Exotel accepted the call (${r.call.exotel_status || "queued"}). Your phone rings first.`);
      onOpenChange(false);
      onPlaced();
    } catch (e) {
      toast.error(err(e, "Exotel didn't place the call"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-md">
        <DialogHeader>
          <DialogTitle>Test call through Exotel</DialogTitle>
          <DialogDescription>
            Exotel rings your phone, then connects the number below from LIFTR&apos;s number. It&apos;s recorded and rated like a real call
            (see the Calls tab), but there&apos;s no lead, no WhatsApp and nothing goes to GoHighLevel.
          </DialogDescription>
        </DialogHeader>
        {phones.length === 0 ? (
          <p className="text-sm text-amber-200">Add your phone under <b>My phones</b> as <b>Via Exotel</b> first.</p>
        ) : (
          <div className="space-y-2">
            <select value={phoneId} onChange={(e) => setPhoneId(e.target.value)}
              className="h-9 w-full rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm" data-testid="test-call-phone">
              {phones.map((p) => <option key={p.id} value={p.id}>{p.label} · {p.number}</option>)}
            </select>
            <Input value={to} onChange={(e) => setTo(e.target.value)} placeholder="Number to call, e.g. 98450 12345" inputMode="tel" data-testid="test-call-to" />
          </div>
        )}
        <DialogFooter>
          <Button onClick={place} disabled={busy || !phoneId || !to.trim()} data-testid="test-call-place">{busy ? "Calling…" : "Call"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function PhonesDialog({ open, onOpenChange, phones, onSaved }) {
  const [rows, setRows] = useState([]);
  useEffect(() => { if (open) setRows(phones.length ? phones.map((p) => ({ ...p })) : [{ label: "Work mobile", number: "", mode: "exotel" }]); }, [open, phones]);
  const set = (i, k, v) => setRows((r) => r.map((row, j) => (j === i ? { ...row, [k]: v } : row)));
  const save = async () => {
    try {
      await saveNurturePhones(rows.filter((r) => r.number.trim() || r.mode === "voiger"));
      toast.success("Phones saved");
      onOpenChange(false);
      onSaved();
    } catch (e) {
      toast.error(err(e, "Couldn't save"));
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-lg">
        <DialogHeader>
          <DialogTitle>My phones</DialogTitle>
          <DialogDescription>
            <b>Via Exotel</b>: Exotel rings this phone first, then connects the lead from LIFTR&apos;s number, and records the call.
            {" "}<b>Direct</b>: dials from this phone itself. Logged, but not recorded.
            {" "}<b>Voiger (Dubai)</b>: you dial in Voiger; FPA picks the call up from Voiger (recorded and scored). No number needed.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_auto_auto] gap-2 items-center">
              <Input value={r.label} onChange={(e) => set(i, "label", e.target.value)} placeholder="Name, e.g. Work mobile" />
              <Input value={r.number} onChange={(e) => set(i, "number", e.target.value)} placeholder={r.mode === "voiger" ? "Not needed" : "98450 12345"}
                inputMode="tel" disabled={r.mode === "voiger"} />
              <select value={r.mode} onChange={(e) => set(i, "mode", e.target.value)}
                className="h-9 rounded-md border border-[#262626] bg-[#0b0b0b] px-2 text-sm">
                <option value="exotel">Via Exotel</option>
                <option value="direct">Direct</option>
                <option value="voiger">Voiger (Dubai) — dial in Voiger</option>
              </select>
              <button onClick={() => setRows((rs) => rs.filter((_, j) => j !== i))} aria-label="Remove phone" className="text-zinc-500 hover:text-rose-400">
                <Trash2 className="h-4 w-4" />
              </button>
            </div>
          ))}
          {rows.length < 6 && (
            <Button variant="outline" size="sm" onClick={() => setRows((r) => [...r, { label: "", number: "", mode: "direct" }])}>
              <Plus className="h-4 w-4 mr-1" /> Add a phone
            </Button>
          )}
        </div>
        <DialogFooter><Button onClick={save} data-testid="save-phones">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function OutcomeDialog({ value, onClose, onSaved, onCallAgain }) {
  const [outcome, setOutcome] = useState("");
  const [note, setNote] = useState("");
  const [when, setWhen] = useState("");
  const [callId, setCallId] = useState(null);
  const [kind, setKind] = useState(null);
  const [phoneId, setPhoneId] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setOutcome(value?.preset || ""); setNote(""); setWhen("");
    if (!value) return;
    if (value.call) { setCallId(value.call.id); setKind(value.call.kind || null); setPhoneId(value.call.phone_id || null); return; }
    // "Log outcome" on a card: attach it to that lead's latest call by me.
    listNurtureCalls(30).then((d) => {
      const c = d.calls.find((x) => x.lead_id === value.lead.id) || {};
      setCallId(c.id || null); setKind(c.kind || null); setPhoneId(c.phone_id || null);
    }).catch(() => setCallId(null));
  }, [value]);
  const confirmCall = kind === "confirm";
  const choices = confirmCall ? CONFIRM_OUTCOMES : OUTCOMES;
  const save = async () => {
    if (!outcome) { toast.error("Pick what happened"); return; }
    if (!callId) { toast.error("Call the lead first, then log the outcome"); return; }
    try {
      const res = await setNurtureOutcome(callId, { outcome, note, callback_at: outcome === "callback" && when ? new Date(when).toISOString() : null });
      if (res?.note) toast.warning(res.note, { duration: 10000 });
      else toast.success("Saved");
      onClose();
      onSaved(value?.lead?.id);
    } catch (e) {
      toast.error(err(e, "Couldn't save"));
    }
  };
  // "Call again": this call is saved as No answer (with the note), then the lead is dialled straight away.
  const again = async () => {
    if (!callId) { onCallAgain(value.lead, phoneId); return; }
    setBusy(true);
    try {
      await setNurtureOutcome(callId, { outcome: "no_answer", note, callback_at: null });
      onSaved(value.lead.id);
      onCallAgain(value.lead, phoneId);
    } catch (e) {
      toast.error(err(e, "Couldn't save"));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={!!value} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-[#0f0f0f] border-[#262626] text-zinc-200 max-w-lg max-h-[92dvh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{confirmCall ? `Is ${value?.lead?.name} coming?` : `How did the call with ${value?.lead?.name} go?`}</DialogTitle>
          <DialogDescription>
            {confirmCall
              ? (outcome === "reschedule" ? "Move the appointment in GoHighLevel while you're on the call. The booker stays the owner; FPA picks up the new time."
                : outcome === "cancelling" ? "Cancel it in GoHighLevel too. The lead goes to 4 Week Follow Up."
                  : outcome === "no_answer" ? "One more try in about 45 minutes, inside the window. Two no-answers = unconfirmed."
                    : "Save this after you hang up.")
              : value?.call?.mode === "exotel" || value?.call?.mode === "voiger" ? "Save this after you hang up." : "Save this after you hang up. This call isn't recorded."}
          </DialogDescription>
        </DialogHeader>
        {value?.lead && <LeadNotes leadId={value.lead.id} prominent max={5} />}
        <div className="flex flex-wrap gap-2">
          {choices.map(([k, label]) => (
            <button key={k} onClick={() => setOutcome(k)} data-testid={`outcome-${k}`}
              className={`rounded-full px-3 py-1.5 text-sm border ${outcome === k ? "border-rose-500 bg-rose-500/10 text-white" : "border-[#262626] text-zinc-400"}`}>
              {label}
            </button>
          ))}
        </div>
        {outcome === "callback" && (
          <Input type="datetime-local" value={when} onChange={(e) => setWhen(e.target.value)} aria-label="Call back at" />
        )}
        {outcome === "booked" && !confirmCall && value?.lead && (
          <>
            <NurtureCalendar lead={value.lead} callId={callId} onBooked={() => { onClose(); onSaved(value.lead.id); }} />
            <p className="text-[11px] text-zinc-500">Pick a slot and book it here, or book it in GoHighLevel and press Save.</p>
          </>
        )}
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} placeholder={outcome === "booked" ? "Day and time of the consult" : outcome === "reschedule" ? "New day and time" : "Note (optional)"} />
        <DialogFooter className="gap-2 sm:gap-0">
          <Button variant="outline" onClick={onClose}>Later</Button>
          {!confirmCall && value?.lead?.phone && (
            <Button variant="outline" onClick={again} disabled={busy} data-testid="outcome-call-again">No answer · call again</Button>
          )}
          <Button onClick={save} disabled={busy} data-testid="save-outcome">Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ScoreChip({ score }) {
  if (score == null) return <span className="text-zinc-600">—</span>;
  const tone = score >= 75 ? "text-emerald-400" : score >= 50 ? "text-amber-300" : "text-rose-400";
  return <span className={`font-semibold ${tone}`}>{score}</span>;
}

function CallsTab({ canManage, rep, onClearRep }) {
  const [days, setDays] = useState(7);
  const [calls, setCalls] = useState(null);
  const [open, setOpen] = useState(null);
  const repId = rep?.user_id;
  useEffect(() => { listNurtureCalls(days, repId).then((d) => setCalls(d.calls)).catch(() => setCalls([])); }, [days, repId]);
  if (!calls) return <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-zinc-400">
        Last
        {[1, 7, 30].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`rounded-full px-3 py-1 border ${days === d ? "border-rose-500 text-white" : "border-[#262626]"}`}>
            {d === 1 ? "24 h" : `${d} days`}
          </button>
        ))}
        <span className="ml-2">{calls.length} calls</span>
        {rep && (
          <button onClick={onClearRep} className="ml-2 rounded-full border border-rose-500/50 bg-rose-500/10 px-3 py-1 text-white" data-testid="calls-rep-filter">
            {rep.name} only ✕
          </button>
        )}
      </div>
      {calls.length === 0 ? (
        <div className="card-surface p-8 text-center text-sm text-zinc-500">No calls yet.</div>
      ) : (
        <div className="card-surface divide-y divide-[#1a1a1a]">
          {calls.map((c) => (
            <div key={c.id}>
              <button className="w-full text-left px-4 py-3 grid grid-cols-[1fr_auto] md:grid-cols-[150px_1fr_160px_70px_60px] gap-x-4 gap-y-1 items-center hover:bg-[#111]"
                onClick={() => setOpen(open === c.id ? null : c.id)}>
                <span className="text-xs text-zinc-500 hidden md:block">{new Date(c.created_at).toLocaleString([], { dateStyle: "short", timeStyle: "short" })}</span>
                <span className="min-w-0 truncate text-sm">
                  {c.mode === "voiger" && (
                    <span className="mr-1.5 rounded border border-sky-500/40 bg-sky-500/10 px-1.5 py-0.5 text-[10px] text-sky-200" data-testid={`voiger-badge-${c.id}`}>
                      Voiger {c.direction === "inbound" ? "↙ in" : "↗ out"}
                    </span>
                  )}
                  <b>{c.lead_name}</b> <span className="text-zinc-500">· {shortGym(c.gym)}{canManage ? ` · ${c.user_name}` : ""}</span>
                </span>
                <span className="text-xs text-zinc-400 hidden md:block">{MODE_LABEL[c.mode]}{c.outcome ? ` · ${OUTCOME_LABEL[c.outcome]}` : ""}</span>
                <span className="text-xs text-zinc-400 hidden md:block">{c.talk_seconds ? secs(c.talk_seconds) : (c.mode === "exotel" ? (c.status === "dialling" ? "…" : "no answer")
                  : c.mode === "voiger" ? (c.status === "dial_in_voiger" ? "…" : c.direction === "inbound" ? "missed" : "no answer") : "")}</span>
                <span className="text-sm text-right"><ScoreChip score={c.rating?.overall_score} /></span>
              </button>
              {open === c.id && <CallDetail id={c.id} canManage={canManage} />}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Measure({ label, value, hint }) {
  return (
    <div className="rounded-md border border-[#1f1f1f] px-3 py-2">
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className="text-sm font-semibold mt-0.5">{value ?? "—"}</div>
      {hint && <div className="text-[10px] text-zinc-500">{hint}</div>}
    </div>
  );
}

function CallDetail({ id, canManage }) {
  const [c, setC] = useState(null);
  useEffect(() => { getNurtureCall(id).then(setC).catch(() => setC({ error: true })); }, [id]);
  if (!c) return <div className="px-4 pb-4 text-xs text-zinc-500">Loading…</div>;
  if (c.error) return <div className="px-4 pb-4 text-xs text-rose-400">Couldn&apos;t load this call</div>;
  const r = c.rating; const m = c.metrics || {}; const v = c.voice;
  return (
    <div className="px-4 pb-5 space-y-4 text-sm" data-testid={`call-detail-${id}`}>
      {c.note && <div className="text-zinc-400">Note: {c.note}</div>}
      {c.has_audio ? <audio controls preload="none" src={nurtureCallAudioUrl(id)} className="w-full max-w-xl" />
        : <div className="text-xs text-zinc-500">{c.mode === "exotel" || c.mode === "voiger"
          ? (c.talk_seconds ? (c.recording_state === "none" ? "Voiger had no recording for this call." : "Recording on its way…")
            : c.status === "dial_in_voiger" ? "Waiting for the call to show up in Voiger…" : "No recording: the call didn't connect.")
          : "Not recorded (called outside Exotel)."}</div>}
      {c.analysis_status && c.analysis_status !== "rated" && (
        <div className="text-xs text-zinc-500">
          Analysis: {c.analysis_status}{c.error ? ` · ${c.error}` : ""}
          {canManage && (c.analysis_status === "error" || c.analysis_status === "stored") && (
            <button className="ml-2 underline" onClick={() => reprocessNurtureCall(id).then(() => toast("Re-running"))}>Try again</button>
          )}
        </div>
      )}
      {(c.metrics || v) && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          <Measure label="Rep talking" value={m.rep_talk_pct != null ? `${m.rep_talk_pct}%` : null} hint="40–60% is healthy" />
          <Measure label="Pace" value={m.rep_words_per_minute ? `${m.rep_words_per_minute} wpm` : null} hint="130–170 is clear" />
          <Measure label="Questions asked" value={m.rep_questions} />
          <Measure label="Longest silence" value={m.longest_silence_seconds != null ? `${m.longest_silence_seconds}s` : null} />
          <Measure label="Voice" value={v ? v.label : null} hint={v ? `pitch moves ${v.pitch_spread_semitones} semitones` : "needs a two-channel recording"} />
          <Measure label="Loudness range" value={v ? `${v.loudness_spread_db} dB` : null} />
          <Measure label="Talked over lead" value={m.rep_interruptions} />
          <Measure label="Call length" value={secs(m.duration_seconds)} />
        </div>
      )}
      {r && (
        <div className="space-y-3">
          <div className="flex flex-wrap gap-4 items-baseline">
            <div>Overall <span className="text-xl"><ScoreChip score={r.overall_score} /></span></div>
            <div className="text-zinc-400">Script {r.adherence_pct}% · Tone {r.tone_score}</div>
            {r.booked_for && <div className="text-emerald-400">Booked: {r.booked_for}</div>}
            {c.script_title ? <div className="text-xs text-zinc-500">vs {c.script_title}</div> : <div className="text-xs text-amber-400/80">vs the default checklist (no script added yet)</div>}
          </div>
          <p className="text-zinc-300">{r.summary}</p>
          {r.tone_note && <p className="text-zinc-400"><b className="text-zinc-300">Voice and tone:</b> {r.tone_note}</p>}
          <div className="space-y-1">
            {(r.part_scores || []).map((p) => (
              <div key={p.part} className="grid grid-cols-[1fr_40px] md:grid-cols-[220px_40px_1fr] gap-2 text-xs">
                <span className="text-zinc-300">{p.part}</span><ScoreChip score={p.score} /><span className="text-zinc-500 col-span-2 md:col-span-1">{p.verdict}</span>
              </div>
            ))}
          </div>
          {r.coaching_feedback && <p className="text-zinc-300"><b>Coaching:</b> {r.coaching_feedback}</p>}
        </div>
      )}
      {c.segments?.length > 0 && (
        <details>
          <summary className="cursor-pointer text-xs text-zinc-400">Transcript</summary>
          <div className="mt-2 space-y-1 max-h-80 overflow-y-auto text-xs">
            {c.segments.map((s, i) => (
              <div key={i} className={s.speaker === "rep" ? "text-zinc-200" : "text-sky-300"}>
                <span className="text-zinc-600 mr-2">{secs(s.start)}</span>
                <b className="mr-1">{s.speaker === "rep" ? "Rep" : s.speaker === "lead" ? "Lead" : ""}</b>{s.text}
              </div>
            ))}
          </div>
        </details>
      )}
    </div>
  );
}

// "Dial in Voiger now": the rep dials the lead in Voiger; FPA links that call to this one when it syncs.
function VoigerDial({ voiger, first }) {
  const copy = () => navigator.clipboard?.writeText(voiger.number).then(() => toast.success("Number copied")).catch(() => {});
  return (
    <div className="rounded-md border border-sky-500/40 bg-sky-500/[0.06] px-3 py-3 space-y-2" data-testid="voiger-dial">
      <div className="text-sm font-semibold text-sky-200">{voiger.message || "Dial in Voiger now"}</div>
      <div className="flex items-center gap-2">
        <span className="font-mono text-lg">{voiger.number}</span>
        <Button size="sm" variant="outline" className="h-7 px-2.5 text-xs" onClick={copy} data-testid="voiger-copy">Copy</Button>
        {voiger.tel && <a href={voiger.tel} className="text-xs text-zinc-400 underline">tel: link</a>}
      </div>
      <p className="text-[11px] text-zinc-500">Call {first} from Voiger&apos;s Dubai number. FPA picks the call up from Voiger within a few minutes
        (recording and score included) and links it to this one.</p>
    </div>
  );
}

// Managers: is FPA pulling the Dubai calls from Voiger?
function VoigerChip() {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { getVoigerStatus().then(setSt).catch(() => setSt(null)); }, []);
  if (!st) return null;
  const syncNow = async () => {
    setBusy(true);
    try {
      const r = await voigerSyncNow();
      setSt(r.status);
      toast.success(`Voiger: ${r.calls} call${r.calls === 1 ? "" : "s"} read`);
    } catch (e) {
      toast.error(err(e, "Voiger sync failed"));
      getVoigerStatus().then(setSt).catch(() => {});
    } finally {
      setBusy(false);
    }
  };
  const tone = !st.configured ? "border-[#262626] text-zinc-500" : st.last_error ? "border-amber-500/50 text-amber-300" : "border-sky-500/40 text-sky-200";
  const title = !st.configured ? `Not set up: ${st.missing.join(", ")}`
    : st.last_error ? `Last error: ${st.last_error}` : `Auth: ${st.auth_mode} · last sync ${st.last_sync_at ? ago(st.last_sync_at) : "never"}`;
  return (
    <span className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-xs ${tone}`} title={title} data-testid="voiger-status">
      Voiger: {!st.configured ? "not set up" : !st.sync_on ? "sync off" : st.last_error ? "error" : `${st.calls_synced} calls`}
      {st.configured && st.last_sync_at && <span className="text-zinc-500">· {ago(st.last_sync_at)}</span>}
      {st.configured && (
        <button onClick={syncNow} disabled={busy} className="underline disabled:opacity-50" data-testid="voiger-sync-now">{busy ? "syncing…" : "sync now"}</button>
      )}
    </span>
  );
}

function TeamTab() {
  const [days, setDays] = useState(7);
  const [rows, setRows] = useState(null);
  const [unassigned, setUnassigned] = useState(0);
  useEffect(() => {
    getNurtureTeam(days).then((d) => { setRows(d.reps); setUnassigned(d.overdue_unassigned || 0); }).catch(() => setRows([]));
  }, [days]);
  if (!rows) return <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>;
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-sm text-zinc-400">
        Last
        {[7, 30].map((d) => (
          <button key={d} onClick={() => setDays(d)} className={`rounded-full px-3 py-1 border ${days === d ? "border-rose-500 text-white" : "border-[#262626]"}`}>{d} days</button>
        ))}
        <span className="ml-auto"><VoigerChip /></span>
      </div>
      <div className="card-surface overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-[11px] uppercase tracking-wider text-zinc-500">
            <tr className="text-left">
              {["Rep", "Calls", "Recorded", "Not recorded", "Pickup", "Booked", "Booked today", "Booked this week", "Overdue redials", "Avg score", "Rep talking", "Voice movement", "Minutes to first call"].map((h) => (
                <th key={h} className="px-3 py-2 font-medium whitespace-nowrap">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {rows.length === 0 && <tr><td colSpan={13} className="px-3 py-6 text-center text-zinc-500">No calls in this period.</td></tr>}
            {rows.map((r) => (
              <tr key={r.user_id}>
                <td className="px-3 py-2 font-medium">{r.name}</td>
                <td className="px-3 py-2">{r.calls}</td>
                <td className="px-3 py-2">{r.exotel + (r.voiger || 0)}</td>
                <td className={`px-3 py-2 ${(r.direct + (r.whatsapp || 0)) > r.exotel + (r.voiger || 0) ? "text-amber-300" : ""}`}>{r.direct + (r.whatsapp || 0)}</td>
                <td className="px-3 py-2">{r.pickup_pct != null ? `${r.pickup_pct}%` : "—"}</td>
                <td className="px-3 py-2">{r.booked}</td>
                <td className={`px-3 py-2 ${r.booked_today ? "text-emerald-300 font-semibold" : "text-zinc-500"}`}>{r.booked_today || 0}</td>
                <td className="px-3 py-2">{r.booked_week || 0}</td>
                <td className={`px-3 py-2 ${r.overdue_redials ? "text-rose-300 font-semibold" : "text-zinc-500"}`}>{r.overdue_redials || 0}</td>
                <td className="px-3 py-2"><ScoreChip score={r.avg_score != null ? Math.round(r.avg_score) : null} /> <span className="text-[10px] text-zinc-500">{r.rated ? `(${r.rated})` : ""}</span></td>
                <td className="px-3 py-2">{r.avg_rep_talk_pct != null ? `${Math.round(r.avg_rep_talk_pct)}%` : "—"}</td>
                <td className="px-3 py-2">{r.avg_pitch_spread != null ? `${r.avg_pitch_spread} st` : "—"}</td>
                <td className="px-3 py-2">{r.median_minutes_to_first_call ?? "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-zinc-500">
        {unassigned > 0 && <span className="text-rose-300">{unassigned} overdue lead{unassigned > 1 ? "s have" : " has"} never been called. </span>}
        Nobody owns a lead before booking, so overdue redials (next attempt or 4-week callback over an hour late) count against whoever called the lead last.
        Booked today / this week count consults booked on the rep&apos;s calls (India time, week from Monday).
        Pickup counts Exotel calls only. &quot;Not recorded&quot; = direct and WhatsApp calls; amber when a rep makes more of those than recorded ones.
        Voice movement is how far the rep&apos;s pitch moves (semitones): under 3 sounds flat.
      </p>
    </div>
  );
}

function ScriptTab({ canManage, gyms }) {
  const [doc, setDoc] = useState(null);
  const [title, setTitle] = useState("");
  const [text, setText] = useState("");
  useEffect(() => {
    getNurtureScript().then((d) => { setDoc(d); setTitle(d.title || "Lead Nurture script"); setText(d.text || ""); }).catch(() => setDoc({}));
  }, []);
  if (!doc) return <div className="card-surface p-8 text-center text-sm text-zinc-500">Loading…</div>;
  const save = async () => {
    try {
      setDoc(await saveNurtureScript({ title, text }));
      toast.success("Script saved. New calls are rated against it.");
    } catch (e) {
      toast.error(err(e, "Couldn't save"));
    }
  };
  return (
    <div className="space-y-3 max-w-3xl">
      <p className="text-sm text-zinc-400">
        Recorded calls are rated against this script, part by part. Write it in numbered parts (PART 1 Opening, PART 2 …) and say what each part should cover.
        {!doc.text && " Until it's added, calls are rated against a default checklist (shown below)."}
      </p>
      <Input value={title} onChange={(e) => setTitle(e.target.value)} disabled={!canManage} />
      <Textarea value={text} onChange={(e) => setText(e.target.value)} disabled={!canManage} rows={18}
        placeholder={doc.default || ""} className="font-mono text-xs" data-testid="script-text" />
      {canManage ? <Button onClick={save} data-testid="save-script">Save script</Button>
        : <p className="text-xs text-zinc-500">Only managers can change the script.</p>}
      {doc.updated_by && <p className="text-xs text-zinc-500">Last saved by {doc.updated_by}</p>}
      <ConsultHours />
      <ConfirmSettings canManage={canManage} gyms={gyms} />
      <ReplySettings canManage={canManage} gyms={gyms} />
      <NurtureIncentives />
    </div>
  );
}

// Each gym's consult times, read from its GoHighLevel consult calendar (or the defaults when it can't be read).
function ConsultHours() {
  const [rows, setRows] = useState(null);
  const load = (refresh) => getNurtureConsultHours(refresh).then((d) => setRows(d.gyms)).catch(() => setRows([]));
  useEffect(() => { load(false); }, []);
  const days = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
  // Collapse identical weekdays: "Mon–Sat 07:00–10:00, 16:30–20:00 · Sun closed".
  const summary = (week) => {
    const parts = [];
    days.forEach((d, i) => {
      const v = (week[d] || []).join(", ") || "closed";
      const last = parts[parts.length - 1];
      if (last && last.v === v && last.end === i - 1) { last.end = i; } else { parts.push({ start: i, end: i, v }); }
    });
    return parts.map((p) => `${days[p.start]}${p.end > p.start ? `–${days[p.end]}` : ""} ${p.v}`).join(" · ");
  };
  return (
    <div className="card-surface p-4 space-y-2" data-testid="consult-hours">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">Consult times (from GoHighLevel)</h3>
        <button className="text-xs text-zinc-400 underline" onClick={() => { setRows(null); load(true); }}>Read again</button>
      </div>
      <p className="text-[11px] text-zinc-500">Confirmation windows follow these: evening consults are confirmed from 3:30 pm (or 90 min before the evening&apos;s first slot), morning consults 7–8 pm the evening before.</p>
      {!rows ? <div className="text-xs text-zinc-500">Loading…</div> : (
        <div className="overflow-x-auto -mx-4 px-4">
          <table className="w-full text-xs">
            <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
              <tr className="text-left"><th className="py-1.5 pr-3 font-medium">Gym</th><th className="py-1.5 pr-3 font-medium">Consult times</th><th className="py-1.5 font-medium">Source</th></tr>
            </thead>
            <tbody className="divide-y divide-[#1a1a1a]">
              {rows.map((g) => (
                <tr key={g.facility_id}>
                  <td className="py-1.5 pr-3 whitespace-nowrap">{shortGym(g.gym)}</td>
                  <td className="py-1.5 pr-3">{summary(g.week)}{g.slot_minutes ? <span className="text-zinc-500"> · {g.slot_minutes}-min slots</span> : null}</td>
                  <td className={`py-1.5 ${g.source === "ghl" ? "text-zinc-400" : "text-amber-400/80"}`} title={g.reason || undefined}>
                    {g.source === "ghl" ? (g.calendars || []).map((c) => c.name).join(", ") || "GoHighLevel" : "Default hours (calendar not readable)"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

const CFG_FIELDS = [["evening_from", "Evening: confirm from"], ["morning_from", "Morning: evening before, from"], ["morning_until", "…until"]];

// Confirmation windows: defaults for every gym, plus per-gym overrides (managers).
// Lead nurture incentive slabs, per country (moved here from each gym's Settings). They change
// pay, so only a Master Admin edits them; everyone on the team can see them.
function NurtureIncentives() {
  const { user } = useAuth();
  return (
    <div className="space-y-3 pt-2" data-testid="nurture-incentives">
      <div>
        <div className="eyebrow">Lead nurture incentive slabs</div>
        <p className="text-xs text-zinc-500 mt-1">Per country, in that country&apos;s currency. Paid to the executive whose lead became a sale.</p>
      </div>
      <IncentiveCountries fields={["leads"]} canEdit={atLeast(user?.role, "master_admin")} />
    </div>
  );
}

function ConfirmSettings({ canManage, gyms }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => { getNurtureSettings().then(setDoc).catch(() => setDoc(null)); }, []);
  if (!doc) return null;
  const val = (fid, k) => (fid ? doc.gyms?.[fid]?.confirm?.[k] : doc.confirm?.[k]) || "";
  const set = (fid, k, v) => setDoc((d) => {
    if (!fid) return { ...d, confirm: { ...d.confirm, [k]: v || undefined } };
    const g = { ...(d.gyms?.[fid]?.confirm || {}), [k]: v || undefined };
    return { ...d, gyms: { ...d.gyms, [fid]: { confirm: g } } };
  });
  const save = async () => {
    try {
      const strip = (o) => Object.fromEntries(Object.entries(o || {}).filter(([, v]) => v));
      const out = await saveNurtureSettings({ confirm: strip(doc.confirm),
        gyms: Object.fromEntries(Object.entries(doc.gyms || {}).map(([fid, g]) => [fid, { confirm: strip(g.confirm) }])) });
      setDoc((d) => ({ ...d, ...out }));
      toast.success("Confirmation windows saved");
    } catch (e) {
      toast.error(err(e, "Couldn't save"));
    }
  };
  const row = (fid, label) => (
    <tr key={fid || "all"}>
      <td className="py-1.5 pr-3 whitespace-nowrap">{label}</td>
      {CFG_FIELDS.map(([k]) => (
        <td key={k} className="py-1.5 pr-2">
          <input type="time" value={val(fid, k)} disabled={!canManage} onChange={(e) => set(fid, k, e.target.value)}
            placeholder={fid ? "" : doc.defaults?.[k]} aria-label={`${label} ${k}`}
            className="h-7 rounded-md border border-[#262626] bg-[#0b0b0b] px-1.5 text-xs text-zinc-200" />
        </td>
      ))}
    </tr>
  );
  return (
    <div className="card-surface p-4 space-y-2" data-testid="confirm-settings">
      <h3 className="text-sm font-semibold">Confirmation call windows</h3>
      <p className="text-[11px] text-zinc-500">
        Defaults: evening {doc.defaults.evening_from} · morning {doc.defaults.morning_from}–{doc.defaults.morning_until} the evening before.
        Blank = default. A booking made within {doc.defaults.booked_within_hours} h of the window opening counts as confirmed at booking.
      </p>
      <div className="overflow-x-auto -mx-4 px-4">
        <table className="text-xs">
          <thead className="text-[10px] uppercase tracking-wider text-zinc-500">
            <tr className="text-left"><th className="py-1.5 pr-3 font-medium">Gym</th>{CFG_FIELDS.map(([k, l]) => <th key={k} className="py-1.5 pr-2 font-medium whitespace-nowrap">{l}</th>)}</tr>
          </thead>
          <tbody className="divide-y divide-[#1a1a1a]">
            {row(null, "Every gym")}
            {gyms.map((g) => row(g.id, shortGym(g.name)))}
          </tbody>
        </table>
      </div>
      {canManage && <Button size="sm" onClick={save} data-testid="save-confirm-settings">Save windows</Button>}
    </div>
  );
}

// WhatsApp replies to the heads-up (routes/nurture_reply.py): per gym on/off and the two messages FPA may send.
function ReplySettings({ canManage, gyms }) {
  const [doc, setDoc] = useState(null);
  useEffect(() => {
    getNurtureSettings().then((d) => setDoc({ ...(d.reply || {}), gyms_off: d.reply?.gyms_off || [], defaults: d.reply_defaults || {} }))
      .catch(() => setDoc(null));
  }, []);
  if (!doc) return null;
  const off = new Set(doc.gyms_off);
  const toggle = (fid) => setDoc((d) => ({ ...d, gyms_off: off.has(fid) ? d.gyms_off.filter((g) => g !== fid) : [...d.gyms_off, fid] }));
  const save = async () => {
    try {
      const out = await saveNurtureSettings({ reply: { followup_text: doc.followup_text || null, confirm_text: doc.confirm_text || null, gyms_off: doc.gyms_off } });
      setDoc((d) => ({ ...d, ...(out.reply || {}) }));
      toast.success("WhatsApp reply settings saved");
    } catch (e) {
      toast.error(err(e, "Couldn't save"));
    }
  };
  const choices = (doc.defaults.choices || []).map((c, i) => `${i + 1}. ${c}`).join("\n");
  return (
    <div className="card-surface p-4 space-y-3" data-testid="reply-settings">
      <div>
        <h3 className="text-sm font-semibold">WhatsApp replies to the heads-up</h3>
        <p className="text-[11px] text-zinc-500">
          FPA reads each lead&apos;s WhatsApp for 30 minutes after the heads-up. &quot;Not now&quot; cancels the call; FPA then asks once when to call back and sets the callback from their answer.
          Needs the conversations.readonly, conversations/message.readonly and conversations/message.write scopes on the gym&apos;s GoHighLevel token. Turn a gym off if its token doesn&apos;t have them.
        </p>
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1.5">
        {gyms.map((g) => (
          <label key={g.id} className="flex items-center gap-1.5 text-xs text-zinc-300">
            <input type="checkbox" checked={!off.has(g.id)} disabled={!canManage} onChange={() => toggle(g.id)} data-testid={`reply-watch-${g.id}`} />
            {shortGym(g.name)}
          </label>
        ))}
      </div>
      <div className="space-y-1">
        <div className="text-[11px] text-zinc-400">Follow-up question ({"{first_name}"}, {"{caller_first_name}"}); the choices are added below it</div>
        <Textarea value={doc.followup_text || ""} onChange={(e) => setDoc((d) => ({ ...d, followup_text: e.target.value }))} disabled={!canManage}
          placeholder={doc.defaults.followup_text} rows={2} className="text-xs" data-testid="reply-followup-text" />
        <pre className="text-[10px] text-zinc-500 whitespace-pre-wrap">{`Reply 1, 2, 3 or 4:\n${choices}\n\nOr just reply with a time.`}</pre>
      </div>
      <div className="space-y-1">
        <div className="text-[11px] text-zinc-400">Confirmation ({"{when}"} = e.g. &quot;tomorrow at 10:00 am&quot;)</div>
        <Input value={doc.confirm_text || ""} onChange={(e) => setDoc((d) => ({ ...d, confirm_text: e.target.value }))} disabled={!canManage}
          placeholder={doc.defaults.confirm_text} className="text-xs" data-testid="reply-confirm-text" />
      </div>
      {canManage && <Button size="sm" onClick={save} data-testid="save-reply-settings">Save</Button>}
    </div>
  );
}
